import React, { createContext, useContext, useMemo, useLayoutEffect, useEffect, useState } from "react";
import { parseColorToRGB } from "src/utils/colorUtils";

const Ctx = createContext({
  delay: 500,
  thickness: 6,
  radius: 6,
  thumb: "rgba(100, 100, 100, 0.5)",
  thumbOpacity: 1,
  track: "transparent",
  mode: "global",
});

export function AutoHideScrollbarProvider({ children, delay = 500, thickness = 6, radius = 6, thumb = "rgba(100, 100, 100, 0.5)", thumbOpacity = 1, track = "transparent", mode = "global" }) {
  const cfg = useMemo(() => ({ delay, thickness, radius, thumb, thumbOpacity, track, mode }), [delay, thickness, radius, thumb, thumbOpacity, track, mode]);

  const [enabled, setEnabled] = useState(true);

  // --- compute enabled and update on resize ---
  useEffect(() => {
    const computeEnabled = () => {
      if (typeof window === "undefined" || typeof navigator === "undefined") return true;
      const ua = navigator.userAgent || "";
      const platform = navigator.platform || "";
      const maxTP = navigator.maxTouchPoints || 0;
      const isIPadOS = platform === "MacIntel" && maxTP > 1;
      const isIOS = /iPad|iPhone|iPod/i.test(ua) || isIPadOS;
      const isAndroid = /Android/i.test(ua);
      const isMobileUA = /Mobi|Mobile|Phone/i.test(ua);
      const isMobileOrTablet = isIOS || isAndroid || isIPadOS || isMobileUA;
      return !isMobileOrTablet;
    };

    setEnabled(computeEnabled());

    const onResize = () => setEnabled(computeEnabled());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // --- inject CSS ---
  useLayoutEffect(() => {
    if (!enabled) return;

    const STYLE_ID = "__ahs_overlay_css__";
    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }

    const baseSelector = cfg.mode === "class" ? ".ahs" : cfg.mode === "attribute" ? '[data-ahs="on"]' : '*:not(.ahs-off):not([data-ahs="off"])';

    styleEl.textContent = `
      ${baseSelector} {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      ${baseSelector}::-webkit-scrollbar { width: 0; height: 0; }

      .__ahs_root {
        position: fixed;
        pointer-events: none;
      }

      .__ahs_track_y, .__ahs_track_x {
        position: absolute;
        opacity: 0;
        transition: opacity 120ms ease;
        pointer-events: auto;
      }

      .__ahs_track_y {
        width: var(--ahs-thickness, ${cfg.thickness}px);
        background: var(--ahs-track, ${cfg.track});
        border-radius: var(--ahs-radius, ${cfg.radius}px);
      }

      .__ahs_track_x {
        height: var(--ahs-thickness, ${cfg.thickness}px);
        background: var(--ahs-track, ${cfg.track});
        border-radius: var(--ahs-radius, ${cfg.radius}px);
      }

      .__ahs_thumb {
        position: absolute;
        background: rgba(var(--highlightColorRGB,0,0,0), var(--highlightOpacity, ${cfg.thumbOpacity}));
        border-radius: var(--ahs-radius, ${cfg.radius}px);
        box-shadow: 0 0 0 1px rgba(0,0,0,.06) inset;
        pointer-events: auto;
        user-select: none;
      }
      .__ahs_thumb:hover { box-shadow: 0 0 0 1px rgba(0,0,0,.12) inset; }

      .__ahs_visible .__ahs_track_y,
      .__ahs_visible .__ahs_track_x { opacity: 1; }
    `;
  }, [enabled, cfg.thickness, cfg.radius, cfg.track, cfg.thumbOpacity, cfg.mode]);

  useEffect(() => {
    if (!enabled) return;

    const timers = new WeakMap();
    const overlays = new Map();
    let lastActiveEl = null;

    const isScrollable = (el) => {
      if (!(el instanceof Element)) return false;
      const style = getComputedStyle(el);
      if (cfg.mode === "class" && !el.classList.contains("ahs")) return false;
      if (cfg.mode === "attribute" && el.getAttribute("data-ahs") !== "on") return false;
      if (cfg.mode === "global") {
        if (el.classList.contains("ahs-off") || el.getAttribute("data-ahs") === "off") return false;
      }
      const canY = (style.overflowY === "auto" || style.overflowY === "scroll") && el.scrollHeight > el.clientHeight;
      const canX = (style.overflowX === "auto" || style.overflowX === "scroll") && el.scrollWidth > el.clientWidth;
      return canX || canY;
    };

    const applyVars = () => {
      const html = document.documentElement;
      html.style.setProperty("--ahs-thickness", `${cfg.thickness}px`);
      html.style.setProperty("--ahs-radius", `${cfg.radius}px`);
      html.style.setProperty("--ahs-track", cfg.track);
      html.style.setProperty("--highlightOpacity", String(cfg.thumbOpacity));
      const cs = getComputedStyle(html);
      const varColor = cs.getPropertyValue("--highlightColor")?.trim();
      const baseColor = varColor || cfg.thumb;
      const rgb = parseColorToRGB(baseColor);
      if (rgb) html.style.setProperty("--highlightColorRGB", rgb);
    };

    const getZIndexForOverlay = (el) => {
      let parent = el;
      while (parent && parent !== document.body) {
        const style = getComputedStyle(parent);
        if (style.position !== "static" && style.zIndex !== "auto") {
          const z = parseInt(style.zIndex, 10);
          if (!isNaN(z)) return z + 1;
        }
        parent = parent.parentElement;
      }
      return 20;
    };

    const createOverlay = (ownerEl) => {
      const root = document.createElement("div");
      root.className = "__ahs_root";
      root.__ahsOwner = ownerEl;
      root.style.zIndex = getZIndexForOverlay(ownerEl);

      const trackY = document.createElement("div");
      trackY.className = "__ahs_track_y";
      const thumbY = document.createElement("div");
      thumbY.className = "__ahs_thumb";
      trackY.appendChild(thumbY);

      const trackX = document.createElement("div");
      trackX.className = "__ahs_track_x";
      const thumbX = document.createElement("div");
      thumbX.className = "__ahs_thumb";
      trackX.appendChild(thumbX);

      root.appendChild(trackY);
      root.appendChild(trackX);
      document.body.appendChild(root);

      // Show thumb on track hover
      const showThumb = () => root.classList.add("__ahs_visible");
      const hideThumb = () => {
        if (!dragState) root.classList.remove("__ahs_visible");
      };
      trackY.addEventListener("mouseenter", showThumb);
      trackX.addEventListener("mouseenter", showThumb);
      trackY.addEventListener("mouseleave", hideThumb);
      trackX.addEventListener("mouseleave", hideThumb);

      return { root, trackY, trackX, thumbY, thumbX };
    };

    const ensureOverlay = (el) => {
      if (!isScrollable(el)) return null;
      let ov = overlays.get(el);
      if (!ov) {
        ov = createOverlay(el);
        overlays.set(el, ov);
      }
      applyVars();
      return ov;
    };

    const destroyOverlay = (el) => {
      const ov = overlays.get(el);
      if (!ov) return;
      ov.root.remove();
      overlays.delete(el);
    };

    const show = (el) => {
      if (!isScrollable(el)) return;

      if (timers.has(el)) window.clearTimeout(timers.get(el));
      const t = window.setTimeout(() => hide(el), cfg.delay);
      timers.set(el, t);
    };

    const hide = (el) => {
      destroyOverlay(el); // Actually removes from DOM
      if (timers.has(el)) {
        window.clearTimeout(timers.get(el));
        timers.delete(el);
      }
    };

    // Positioning and sizing
    const positionOverlay = (el) => {
      const ov = overlays.get(el);
      if (!ov) return;
      const rect = el.getBoundingClientRect();
      const inset = 2;
      const w = rect.width,
        h = rect.height;

      ov.root.style.left = `${rect.left}px`;
      ov.root.style.top = `${rect.top}px`;
      ov.root.style.width = `${w}px`;
      ov.root.style.height = `${h}px`;

      ov.trackY.style.left = `${w - inset - cfg.thickness}px`;
      ov.trackY.style.top = `${inset}px`;
      ov.trackY.style.bottom = `${inset}px`;
      ov.trackY.style.width = `${cfg.thickness}px`;

      ov.trackX.style.left = `${inset}px`;
      ov.trackX.style.right = `${inset}px`;
      ov.trackX.style.top = `${h - inset - cfg.thickness}px`;
      ov.trackX.style.height = `${cfg.thickness}px`;
    };

    // NEW: Function to update positions of ALL overlays
    const updateAllOverlayPositions = () => {
      overlays.forEach((ov, el) => {
        if (el instanceof HTMLElement && document.body.contains(el)) {
          positionOverlay(el);
        }
      });
    };

    const updateOverlay = (el) => {
      if (!isScrollable(el)) {
        destroyOverlay(el);
        return;
      }
      const ov = ensureOverlay(el);
      if (!ov) return;
      positionOverlay(el);

      const { clientHeight, scrollHeight, scrollTop, clientWidth, scrollWidth, scrollLeft } = el;

      // Y axis
      const canY = scrollHeight > clientHeight;
      ov.trackY.style.display = canY ? "block" : "none";
      if (canY) {
        const trackHeight = clientHeight - 4;
        const thumbH = Math.max(24, Math.round((clientHeight / scrollHeight) * trackHeight));
        const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (trackHeight - thumbH) || 0;
        ov.thumbY.style.width = "100%";
        ov.thumbY.style.height = `${thumbH}px`;
        ov.thumbY.style.top = `${2 + thumbTop}px`;
        ov.thumbY.style.left = "0";
      }

      // X axis
      const canX = scrollWidth > clientWidth;
      ov.trackX.style.display = canX ? "block" : "none";
      if (canX) {
        const trackWidth = clientWidth - 4;
        const thumbW = Math.max(24, Math.round((clientWidth / scrollWidth) * trackWidth));
        const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * (trackWidth - thumbW) || 0;
        ov.thumbX.style.height = "100%";
        ov.thumbX.style.width = `${thumbW}px`;
        ov.thumbX.style.top = "0";
        ov.thumbX.style.left = `${2 + thumbLeft}px`;
      }
    };

    // Dragging
    let dragState = null;
    const onThumbMouseDown = (e) => {
      const t = e.target;
      if (!(t && t.classList.contains("__ahs_thumb"))) return;
      const root = t.closest(".__ahs_root");
      const owner = root?.__ahsOwner;
      if (!owner || !isScrollable(owner)) return;

      const isY = t.parentElement.classList.contains("__ahs_track_y");
      e.preventDefault();
      e.stopPropagation();
      dragState = { el: owner, axis: isY ? "y" : "x", startClient: isY ? e.clientY : e.clientX, startScroll: isY ? owner.scrollTop : owner.scrollLeft };
      lastActiveEl = owner;

      document.addEventListener("mousemove", onDragMove, true);
      document.addEventListener("mouseup", onDragEnd, true);
    };

    const onDragMove = (e) => {
      if (!dragState) return;
      const { el, axis, startClient, startScroll } = dragState;
      const ov = overlays.get(el);
      if (!ov) return;

      if (axis === "y") {
        const trackSize = el.clientHeight - 4;
        const scrollable = el.scrollHeight - el.clientHeight;
        const thumbSize = ov.thumbY.getBoundingClientRect().height;
        const maxThumb = trackSize - thumbSize;
        el.scrollTop = Math.min(scrollable, Math.max(0, startScroll + (e.clientY - startClient) * (scrollable / maxThumb)));
      } else {
        const trackSize = el.clientWidth - 4;
        const scrollable = el.scrollWidth - el.clientWidth;
        const thumbSize = ov.thumbX.getBoundingClientRect().width;
        const maxThumb = trackSize - thumbSize;
        el.scrollLeft = Math.min(scrollable, Math.max(0, startScroll + (e.clientX - startClient) * (scrollable / maxThumb)));
      }

      show(el);
      updateOverlay(el);
      // UPDATE: After dragging, update positions of all other overlays
      updateAllOverlayPositions();
    };

    const onDragEnd = () => {
      document.removeEventListener("mousemove", onDragMove, true);
      document.removeEventListener("mouseup", onDragEnd, true);
      dragState = null;
    };

    // Event listeners
    const onScrollCapture = (e) => {
      const t = e.target;
      if (isScrollable(t)) {
        updateOverlay(t);
        show(t);
        lastActiveEl = t;
        // UPDATE: After scrolling, update positions of all other overlays
        updateAllOverlayPositions();
      }
    };

    const onOverlayMouseEnter = (e) => {
      const root = e.target.closest?.(".__ahs_root");
      if (!root) return;
      const owner = root.__ahsOwner;
      if (!owner) return;
      lastActiveEl = owner;
      show(owner);
    };

    const onOverlayMouseLeave = (e) => {
      const root = e.target.closest?.(".__ahs_root");
      if (!root) return;
      const owner = root.__ahsOwner;
      if (!owner) return;
      if (timers.has(owner)) window.clearTimeout(timers.get(owner));
      const t = window.setTimeout(() => hide(owner), Math.max(200, cfg.delay / 2));
      timers.set(owner, t);
    };

    // UPDATE: Enhanced viewport change handler
    const onViewportChange = () => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        overlays.forEach((_ov, el) => {
          if (el instanceof HTMLElement && document.body.contains(el)) {
            updateOverlay(el);
          }
        });
      });
    };

    const onKeyDown = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      const isEditable = e.target.isContentEditable || tag === "input" || tag === "textarea" || e.target.getAttribute("role") === "textbox";
      if (isEditable) return;

      const el = lastActiveEl;
      if (!el || !document.body.contains(el) || !isScrollable(el)) return;
      const line = 40,
        page = Math.max(0, el.clientHeight - 40);
      let handled = false;
      switch (e.key) {
        case "ArrowDown":
          el.scrollTop += line;
          handled = true;
          break;
        case "ArrowUp":
          el.scrollTop -= line;
          handled = true;
          break;
        case "ArrowRight":
          el.scrollLeft += line;
          handled = true;
          break;
        case "ArrowLeft":
          el.scrollLeft -= line;
          handled = true;
          break;
        case "PageDown":
          el.scrollTop += page;
          handled = true;
          break;
        case "PageUp":
          el.scrollTop -= page;
          handled = true;
          break;
        case "Home":
          el.scrollTop = 0;
          handled = true;
          break;
        case "End":
          el.scrollTop = el.scrollHeight;
          handled = true;
          break;
        default:
          break;
      }
      if (handled) {
        e.preventDefault();
        show(el);
        updateOverlay(el);
        // UPDATE: After keyboard scrolling, update positions of all other overlays
        updateAllOverlayPositions();
      }
    };

    document.addEventListener("scroll", onScrollCapture, true);
    document.addEventListener("mouseenter", onOverlayMouseEnter, true);
    document.addEventListener("mouseleave", onOverlayMouseLeave, true);
    document.addEventListener("mousedown", onThumbMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);

    window.addEventListener("resize", onViewportChange, { passive: true });
    window.addEventListener("scroll", onViewportChange, { passive: true });

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.removedNodes.forEach((n) => {
          if (n instanceof HTMLElement) {
            if (overlays.has(n)) destroyOverlay(n);
            n.querySelectorAll?.("*").forEach((child) => {
              if (overlays.has(child)) destroyOverlay(child);
            });
          }
        });
      }
      // UPDATE: After DOM mutations, update all overlay positions
      updateAllOverlayPositions();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("scroll", onScrollCapture, true);
      document.removeEventListener("mouseenter", onOverlayMouseEnter, true);
      document.removeEventListener("mouseleave", onOverlayMouseLeave, true);
      document.removeEventListener("mousedown", onThumbMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange);
      mo.disconnect();
      overlays.forEach((_ov, el) => destroyOverlay(el));
      overlays.clear();
    };
  }, [enabled, cfg.delay, cfg.mode, cfg.radius, cfg.thickness, cfg.thumb, cfg.thumbOpacity, cfg.track]);

  return <Ctx.Provider value={cfg}>{children}</Ctx.Provider>;
}

export function useAutoHideScrollbarConfig() {
  return useContext(Ctx);
}
